<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Segments</h5>
        <p class="page-sub">Group contacts by shared attributes for targeted campaigns</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click.stop="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')">CSV</button>
            <button class="export-opt" @click="doExport('pdf')">PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_segments" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Segment
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search segments..." />
      <input v-model="dateFrom" type="date" class="filter-input" style="max-width:160px" />
      <input v-model="dateTo" type="date" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading segments...</p></div>
      <div v-else-if="!segments.length" class="empty-state">
        <font-awesome-icon icon="object-group" class="empty-icon" />
        <p>No segments found</p>
        <span>Create a segment to group contacts for targeted outreach</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Name</th><th>Type</th><th>Members</th><th>Description</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="s in segments" :key="s.id">
            <td class="name-col">{{ s.name }}</td>
            <td><span :class="['type-badge', `type--${s.type}`]">{{ s.type }}</span></td>
            <td>{{ s.members_count ?? 0 }}</td>
            <td>{{ truncate(s.description, 60) }}</td>
            <td>{{ fmtDate(s.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_segments" class="btn-link btn-link--edit" @click="openEdit(s)">Edit</button>
                <button v-if="permission?.delete_segments" class="btn-link btn-link--del" @click="confirmDelete(s)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta && meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Segment' : 'New Segment' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-group form-group--full">
              <label class="form-label">Name <span class="req">*</span></label>
              <input v-model="form.name" class="form-input" required />
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Type</label>
              <select v-model="form.type" class="form-input">
                <option value="static">Static</option>
                <option value="dynamic">Dynamic</option>
              </select>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Description</label>
              <textarea v-model="form.description" class="form-input" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Segment') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Delete Segment</span>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body"><p>Delete <strong>{{ deleteTarget?.name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: { permission() { return this.store.state.getCurrentUserPermission; } },
  data() {
    return {
      segments: [], meta: null, loading: false, submitting: false,
      search: '', page: 1, modalOpen: false, editing: null, deleteTarget: null,
      form: { name:'', type:'static', description:'' },
      debounceTimer: null,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
    };
  },
  mounted() { this.fetchSegments(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchSegments() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/segments', { params: { search: this.search, page: this.page, from: this.dateFrom || undefined, to: this.dateTo || undefined } });
        this.segments = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load segments'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchSegments(); }, 400); },
    changePage(p) { this.page = p; this.fetchSegments(); },
    applyDateFilter() { this.page = 1; this.fetchSegments(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchSegments(); },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search) params.search = this.search;
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo) params.to = this.dateTo;
        const { data } = await axios.get('/crm/segments/export', { params, responseType: 'blob' });
        const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
        const ext  = format === 'pdf' ? 'pdf' : 'csv';
        const ts   = new Date().toISOString().slice(0, 10);
        const url  = URL.createObjectURL(new Blob([data], { type: mime }));
        const a    = document.createElement('a');
        a.href = url; a.download = `segments_${ts}.${ext}`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openCreate() { this.editing = null; this.form = { name:'', type:'static', description:'' }; this.modalOpen = true; },
    openEdit(s) { this.editing = s.id; this.form = { name: s.name, type: s.type, description: s.description??'' }; this.modalOpen = true; },
    closeModal() { this.modalOpen = false; },
    confirmDelete(s) { this.deleteTarget = s; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) { await axios.put(`/crm/segments/${this.editing}`, this.form); this.toast.success('Segment updated'); }
        else              { await axios.post('/crm/segments', this.form); this.toast.success('Segment created'); }
        this.closeModal(); this.fetchSegments();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try { await axios.delete(`/crm/segments/${this.deleteTarget.id}`); this.toast.success('Deleted'); this.deleteTarget = null; this.fetchSegments(); }
      catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    truncate(s, n) { return s && s.length > n ? s.slice(0, n) + '…' : s ?? '—'; },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;outline:none}
.filter-input:focus{border-color:#004AAD}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--del{color:#e03535}
.type-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.type--static{background:#e0f2fe;color:#075985}
.type--dynamic{background:#ede9fe;color:#6d28d9}
.pagination{display:flex;align-items:center;justify-content:center;gap:12px;padding:14px;border-top:1px solid #f0f4fa}
.pg-btn{padding:6px 14px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-info{font-size:13px;color:#7a90b0}
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:520px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#fff;color:#1a2b45;border:1.5px solid #dde5f0;padding:8px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-export:hover{border-color:#004AAD;color:#004AAD}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1.5px solid #e4eaf2;border-radius:9px;box-shadow:0 8px 28px rgba(0,0,0,.1);z-index:200;min-width:140px;padding:5px 0}
.export-opt{display:block;width:100%;background:none;border:none;padding:9px 16px;text-align:left;font-size:13px;color:#1a2b45;cursor:pointer}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
</style>
